import BigNumber from "bignumber.js";
import { FeeDetails, FixedFeeDetails } from "../types";
export interface CalculatedFees {
	fee: BigNumber;
	gstFee: BigNumber;
	totalFeeWithGst: BigNumber;
}

export const calculateFeesForPreview = (
	baseAmount: BigNumber,
	feeDetails: FeeDetails[],
	gstPercentage: number = 0,
	quantity?: BigNumber,
): CalculatedFees => {
	const result = {
		fee: BigNumber(0),
		gstFee: BigNumber(0),
		totalFeeWithGst: baseAmount,
	};

	feeDetails.forEach((feeDetail) => {
		const { tiers, isGstApplicable } = feeDetail;

		// Find the applicable tier
		const applicableTier = tiers.find((tier) => {
			const { lower_bound, upper_bound } = tier;
			// If quantity is provided, use it for tier comparison instead of baseAmount
			const comparisonValue = quantity || baseAmount;
			return (
				comparisonValue.gte(lower_bound) &&
				(upper_bound === null || comparisonValue.lte(upper_bound))
			);
		});

		if (!applicableTier) {
			return;
		}

		// Calculate fee based on the applicable tier
		const { rate, no_of_years } = applicableTier;
		const fee = baseAmount.multipliedBy(rate).multipliedBy(no_of_years || 1);

		// Calculate GST if applicable
		const gstFee = isGstApplicable
			? fee.multipliedBy(gstPercentage)
			: BigNumber(0);
		const totalFee = fee.plus(gstFee);

		feeDetail.calculatedFee = fee.toFixed(6);
		feeDetail.calculatedGstFee = gstFee.toFixed(6);
		feeDetail.applicableTier = applicableTier;

		// Update totals
		result.fee = result.fee.plus(fee);
		result.gstFee = result.gstFee.plus(gstFee);
		result.totalFeeWithGst = result.totalFeeWithGst.plus(totalFee);
	});

	return result;
};

export const calculateFees = (
	baseAmount: BigNumber,
	fixedFeeDetails: FixedFeeDetails[],
	gstPercentage: number = 0,
): CalculatedFees => {
	const result = {
		fee: BigNumber(0),
		gstFee: BigNumber(0),
		totalFeeWithGst: baseAmount,
	};

	fixedFeeDetails.forEach((fixedFeeDetail) => {
		const { rate, no_of_years } = fixedFeeDetail;
		const fee = baseAmount.multipliedBy(rate).multipliedBy(no_of_years || 1);

		// Calculate GST if applicable
		const gstFee = fixedFeeDetail.isGstApplicable
			? fee.multipliedBy(gstPercentage)
			: BigNumber(0);
		const totalFee = fee.plus(gstFee);

		fixedFeeDetail.calculatedFee = fee.toFixed(6);
		fixedFeeDetail.calculatedGstFee = gstFee.toFixed(6);

		// Update totals
		result.fee = result.fee.plus(fee);
		result.gstFee = result.gstFee.plus(gstFee);
		result.totalFeeWithGst = result.totalFeeWithGst.plus(totalFee);
	});

	return result;
};
